/**
 * Viewport widths the layout is built around (ENG-2840).
 *
 * Deliberately dependency-free — no `import.meta.env`, no store — so it can be imported
 * from plain Node (the e2e specs assert the band boundaries directly) as well as from the
 * app. Don't reach for `@/constants` here: that module reads `import.meta.env`, which only
 * a Vite transform defines. It no longer throws outside one, but it does fall back to an
 * empty env, so every value it derives from one goes silently wrong instead.
 */

// Narrowest viewport width (px) the app is designed for. Below it, VSmallScreenGate covers
// the app with a "use a larger screen" notice instead of letting the user loose in a layout
// that was never built for the width.
//
// There is one floor, and it applies only while `layout-narrow-viewport` is on. The gate
// arrived with the narrow layouts it belongs to, so it rolls back with them: with the flag
// off the app renders at every width exactly as production serves it today, ungated. A
// second floor at `lg` would mean "off" still turned phone traffic away — a state neither
// the rollout nor the rollback ever wanted.
//
// The floor sits a tier below where the sidebar switches forms (`lg`) on purpose: from
// there up the rail is replaced by the mobile navbar, so the content column is the full
// viewport minus padding — 708px at 748px — which is WIDER than the 660px a page gets at
// 1024px with the 300px rail expanded. The floor belongs at the point where the content
// itself stops fitting, not where the chrome changes shape.
//
// The 20px of slack under `md` is deliberate: 768 is a real device width (iPad portrait),
// and a device that reports a few px less — a classic scrollbar or browser-chrome
// deduction — would land exactly on the wrong side of a floor set at 768 and be told to
// find a bigger screen by the one tier it was sized for. The cost is that 748–767 renders
// with `sm:` utilities rather than `md:` ones; the only `md:`-keyed content switches in the
// app are loading skeletons dropping their third item and one long/short label swap.
export const MIN_SUPPORTED_WIDTH = 748;

// Viewport bands for analytics, named after the Tailwind tier each one spans. Registered as
// the `viewport_band` super property (plugins/posthog.js) so every event — the funnel
// events, autocapture, $pageview — can be broken down by width without new events.
//
// Boundaries are the TAILWIND tiers, not the floor: the band answers "which utilities were
// live", which is what explains a layout, and it has to mean the same thing in both flag
// states. So `below-md` holds both gated traffic and the 748–767 slice that is served —
// the gate's own events carry the exact `viewport_width` and `min_supported_width` when that
// distinction is the question.
const VIEWPORT_BANDS = [
    [1280, 'xl-plus'],
    // `lg`, where the sidebar rail appears. Spelled out rather than shared with the floor:
    // they are different questions, and the floor no longer sits here.
    [1024, 'lg-xl'],
    [768, 'md-lg'],
    [0, 'below-md'],
];

// Ordered descending: the first floor the width clears wins.
export const getViewportBand = width => (VIEWPORT_BANDS.find(([min]) => width >= min) || [])[1] || null;
